import type { ServiceRequestDto, ServiceResponseDto, ServiceType } from '../types/api/service';
import { resolveAuthContext } from './auth/authContext';

/**
 * Base class for adapters that call the ServicesController's shared
 * request/response envelope (Account, Sync, Content, Data, Notify, Log —
 * all keyed by RequestType, with a stringified-JSON Payload).
 *
 * Always add new backend calls through `callService()`. Do not call
 * `apiService.processServiceRequest()` directly (it has no token-refresh
 * handling), and do not use `apiService`'s bare convenience wrappers
 * (`syncService()`, `contentService()`, etc. — also no refresh handling,
 * a confirmed trap rather than a shortcut). `callService()` is the one path
 * that builds the ServiceRequestDto envelope and goes through
 * `processServiceRequestWithRefresh`, which owns both the proactive
 * (age-based) and reactive (401) refresh paths behind a single
 * concurrency-guarded refresh (see `ApiService.refreshInFlight`).
 */
export abstract class BaseServiceClient {
  protected constructor(protected readonly apiService: any) {}

  protected async callService(
    requestType: string,
    payload: unknown,
    serviceType: ServiceType = 'sync',
    authOptions?: { userStore?: any; storage?: any }
  ): Promise<ServiceResponseDto> {
    const request: ServiceRequestDto = {
      RequestType: requestType,
      Payload: typeof payload === 'string' ? payload : JSON.stringify(payload),
    };

    return this.callServiceRequest(request, serviceType, authOptions);
  }

  /**
   * Lower-level variant of `callService()` for callers that already have a
   * fully-built `ServiceRequestDto` (e.g. from existing helper functions that
   * construct and stringify the payload themselves). Still routes through
   * `processServiceRequestWithRefresh` and the shared `resolveAuthContext()` —
   * use this instead of reaching into `apiService.processServiceRequestWithRefresh`
   * directly, so there remains exactly one call site per class doing so.
   */
  protected async callServiceRequest(
    request: ServiceRequestDto,
    serviceType: ServiceType = 'sync',
    authOptions?: { userStore?: any; storage?: any }
  ): Promise<ServiceResponseDto> {
    return this.apiService.processServiceRequestWithRefresh(
      request,
      serviceType,
      resolveAuthContext(authOptions)
    );
  }
}
